import { c as _c } from "react/compiler-runtime";
import { Button } from '@/components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Trash2, X } from '@/components/ui/icons/Icons';
import { useI18n } from '@/context/I18nProvider';
import { useCustomClassifications, useCreateCustomClassification, useDeleteCustomClassification } from '@/hooks/useCustomClassifications';
import { getClassificationStyle } from '@/lib/classification-utils';
import { Classification } from '@/services/types/Lead.types';
import Link from 'next/link';
import { useState } from 'react';
import { toast } from 'sonner';
const PRESET_COLORS = [{
  bg: '#FEF3C7',
  text: '#B45309'
}, {
  bg: '#DBEAFE',
  text: '#1D4ED8'
}, {
  bg: '#E0E7FF',
  text: '#4338CA'
}, {
  bg: '#FCE7F3',
  text: '#BE185D'
}, {
  bg: '#D1FAE5',
  text: '#065F46'
}, {
  bg: '#FEE2E2',
  text: '#991B1B'
}, {
  bg: '#F3E8FF',
  text: '#7C3AED'
}, {
  bg: '#E5E7EB',
  text: '#374151'
}];
const BUILTIN_STATUSES = [{
  value: Classification.POSITIVE,
  labelKey: 'conversations.positive'
}, {
  value: Classification.ENGAGED,
  labelKey: 'conversations.engaged'
}, {
  value: Classification.UNDETERMINED,
  labelKey: 'conversations.noAnswer'
}, {
  value: Classification.NEGATIVE,
  labelKey: 'conversations.negative'
}];
type ManageClassificationsModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};
export const ManageClassificationsModal = t0 => {
  const $ = _c(91);
  const {
    open,
    onOpenChange
  } = t0;
  const {
    t
  } = useI18n();
  const tc = t as any;
  const {
    data: t1
  } = useCustomClassifications();
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const customClassifications = t2;
  const mCreate = useCreateCustomClassification();
  const mDelete = useDeleteCustomClassification();
  const [newLabel, setNewLabel] = useState("");
  const [selectedColor, setSelectedColor] = useState(PRESET_COLORS[0]);
  const [showForm, setShowForm] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  let t3;
  if ($[2] !== mCreate || $[3] !== newLabel || $[4] !== selectedColor || $[5] !== tc) {
    t3 = () => {
      const label = newLabel.trim();
      if (!label) {
        return;
      }
      mCreate.mutate({
        label,
        color: selectedColor.bg,
        text_color: selectedColor.text
      }, {
        onSuccess: () => {
          setNewLabel("");
          setShowForm(false);
          toast.success(tc("customClassifications.created"));
        },
        onError: () => {
          toast.error(tc("customClassifications.createError"));
        }
      });
    };
    $[2] = mCreate;
    $[3] = newLabel;
    $[4] = selectedColor;
    $[5] = tc;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleCreate = t3;
  let t4;
  if ($[7] !== mDelete || $[8] !== tc) {
    t4 = id => {
      mDelete.mutate(id, {
        onSuccess: () => {
          setDeletingId(null);
          toast.success(tc("customClassifications.deleted"));
        },
        onError: () => {
          toast.error(tc("customClassifications.deleteError"));
        }
      });
    };
    $[7] = mDelete;
    $[8] = tc;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleDelete = t4;
  let t5;
  if ($[10] !== tc) {
    t5 = tc("customClassifications.title");
    $[10] = tc;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t5) {
    t6 = <DialogHeader><DialogTitle>{t5}</DialogTitle></DialogHeader>;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== tc) {
    t7 = tc("customClassifications.builtIn");
    $[14] = tc;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t7) {
    t8 = <p className="text-xs font-medium text-muted-foreground">{t7}</p>;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== tc) {
    t9 = BUILTIN_STATUSES.map(status => {
      const style = getClassificationStyle(status.value, []);
      return <div key={status.value} className="flex items-center gap-2.5 rounded-md px-3 py-1.5"><div className="h-2.5 w-2.5 rounded-full" style={{
          backgroundColor: style.color
        }} /><span className="text-sm" style={{
          color: style.color
        }}>{tc(status.labelKey)}</span></div>;
    });
    $[18] = tc;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t9) {
    t10 = <div className="space-y-1">{t9}</div>;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t8) {
    t11 = <div className="space-y-1.5">{t8}{t10}</div>;
    $[22] = t10;
    $[23] = t8;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== tc) {
    t12 = tc("customClassifications.custom");
    $[25] = tc;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12) {
    t13 = <p className="text-xs font-medium text-muted-foreground">{t12}</p>;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== showForm || $[30] !== tc) {
    t14 = !showForm && <button className="text-xs text-primary hover:underline" onClick={() => setShowForm(true)}>+ {tc("customClassifications.addNew")}</button>;
    $[29] = showForm;
    $[30] = tc;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t13 || $[33] !== t14) {
    t15 = <div className="flex items-center justify-between">{t13}{t14}</div>;
    $[32] = t13;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== customClassifications.length || $[36] !== showForm || $[37] !== tc) {
    t16 = customClassifications.length === 0 && !showForm && <p className="text-xs text-muted-foreground py-1">{tc("customClassifications.noCustom")}</p>;
    $[35] = customClassifications.length;
    $[36] = showForm;
    $[37] = tc;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== customClassifications || $[40] !== deletingId || $[41] !== handleDelete || $[42] !== mDelete || $[43] !== tc) {
    let t18;
    if ($[45] !== deletingId || $[46] !== handleDelete || $[47] !== mDelete || $[48] !== tc) {
      t18 = cc => <div key={cc.id} className="flex items-center justify-between rounded-md px-3 py-1.5 group"><div className="flex items-center gap-2.5"><div className="h-2.5 w-2.5 rounded-full" style={{
            backgroundColor: cc.text_color
          }} /><span className="text-sm" style={{
            color: cc.text_color
          }}>{cc.label}</span></div>{deletingId === cc.id ? <div className="flex items-center gap-1.5"><span className="text-[11px] text-muted-foreground">{tc("customClassifications.confirmDelete")}</span><Button size="sm" variant="destructive" className="h-6 text-[11px] px-2" onClick={() => handleDelete(cc.id)} disabled={mDelete.isPending}>{tc("customClassifications.confirm")}</Button><button className="text-muted-foreground hover:text-foreground" onClick={() => setDeletingId(null)}><X className="size-3" /></button></div> : <button className="text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-destructive transition-all" onClick={() => setDeletingId(cc.id)}><Trash2 className="size-3.5" /></button>}</div>;
      $[45] = deletingId;
      $[46] = handleDelete;
      $[47] = mDelete;
      $[48] = tc;
      $[49] = t18;
    } else {
      t18 = $[49];
    }
    t17 = customClassifications.map(t18);
    $[39] = customClassifications;
    $[40] = deletingId;
    $[41] = handleDelete;
    $[42] = mDelete;
    $[43] = tc;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[50] !== t17) {
    t18 = <div className="space-y-1">{t17}</div>;
    $[50] = t17;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] !== t15 || $[53] !== t16 || $[54] !== t18) {
    t19 = <div className="space-y-1.5">{t15}{t16}{t18}</div>;
    $[52] = t15;
    $[53] = t16;
    $[54] = t18;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  let t20;
  if ($[56] !== handleCreate || $[57] !== mCreate || $[58] !== newLabel || $[59] !== selectedColor || $[60] !== showForm || $[61] !== tc) {
    t20 = showForm && <div className="rounded-lg border border-gray-200 p-3 space-y-3"><Input value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder={tc("customClassifications.labelPlaceholder")} maxLength={30} className="h-8 text-sm" /><div className="flex flex-wrap gap-1.5">{PRESET_COLORS.map((color, i) => <button key={i} className="h-7 w-7 rounded-full border-2 transition-all" style={{
          backgroundColor: color.bg,
          borderColor: selectedColor === color ? color.text : "transparent"
        }} onClick={() => setSelectedColor(color)}><span className="block h-2.5 w-2.5 rounded-full mx-auto" style={{
            backgroundColor: color.text
          }} /></button>)}</div>{newLabel.trim() && <div className="flex items-center gap-2"><span className="text-xs text-muted-foreground">{tc("customClassifications.preview")}:</span><span className="text-xs font-medium px-2 py-0.5 rounded-md" style={{
          color: selectedColor.text,
          backgroundColor: selectedColor.bg
        }}>{newLabel.trim()}</span></div>}<div className="flex items-center gap-2"><Button size="sm" className="h-7 text-xs" onClick={handleCreate} disabled={!newLabel.trim() || mCreate.isPending}>{tc("customClassifications.create")}</Button><Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => {
          setShowForm(false);
          setNewLabel("");
        }}>{tc("customClassifications.cancel")}</Button></div></div>;
    $[56] = handleCreate;
    $[57] = mCreate;
    $[58] = newLabel;
    $[59] = selectedColor;
    $[60] = showForm;
    $[61] = tc;
    $[62] = t20;
  } else {
    t20 = $[62];
  }
  let t21;
  if ($[63] !== tc) {
    t21 = tc("customClassifications.deleteWarning");
    $[63] = tc;
    $[64] = t21;
  } else {
    t21 = $[64];
  }
  let t22;
  if ($[65] !== t21) {
    t22 = <p className="text-[11px] text-amber-600">{t21}</p>;
    $[65] = t21;
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  let t23;
  if ($[67] !== tc) {
    t23 = tc("customClassifications.filteringCta");
    $[67] = tc;
    $[68] = t23;
  } else {
    t23 = $[68];
  }
  let t24;
  if ($[69] !== t23) {
    t24 = <p className="text-xs text-[#4338CA] mb-2">{t23}</p>;
    $[69] = t23;
    $[70] = t24;
  } else {
    t24 = $[70];
  }
  let t25;
  if ($[71] !== tc) {
    t25 = tc("customClassifications.goToFiltering");
    $[71] = tc;
    $[72] = t25;
  } else {
    t25 = $[72];
  }
  let t26;
  if ($[73] !== t25) {
    t26 = <Link href="/agents?type=filtering"><Button size="sm" variant="outline" className="h-7 text-xs">{t25}</Button></Link>;
    $[73] = t25;
    $[74] = t26;
  } else {
    t26 = $[74];
  }
  let t27;
  if ($[75] !== t24 || $[76] !== t26) {
    t27 = <div className="rounded-lg border border-[#E0E7FF] bg-[#EEF2FF] p-3">{t24}{t26}</div>;
    $[75] = t24;
    $[76] = t26;
    $[77] = t27;
  } else {
    t27 = $[77];
  }
  let t28;
  if ($[78] !== t11 || $[79] !== t19 || $[80] !== t20 || $[81] !== t22 || $[82] !== t27) {
    t28 = <div className="space-y-4">{t11}{t19}{t20}{t22}{t27}</div>;
    $[78] = t11;
    $[79] = t19;
    $[80] = t20;
    $[81] = t22;
    $[82] = t27;
    $[83] = t28;
  } else {
    t28 = $[83];
  }
  let t29;
  if ($[84] !== t28 || $[85] !== t6) {
    t29 = <DialogContent className="max-w-md">{t6}{t28}</DialogContent>;
    $[84] = t28;
    $[85] = t6;
    $[86] = t29;
  } else {
    t29 = $[86];
  }
  let t30;
  if ($[87] !== onOpenChange || $[88] !== open || $[89] !== t29) {
    t30 = <Dialog open={open} onOpenChange={onOpenChange}>{t29}</Dialog>;
    $[87] = onOpenChange;
    $[88] = open;
    $[89] = t29;
    $[90] = t30;
  } else {
    t30 = $[90];
  }
  return t30;
};